import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './context/authStore';
import { useFeatureFlagStore, useFeatureFlag, useFeatureFlagStatus, useFlagLocked, usePermissionOf } from './context/featureFlagStore';
import NotInPlan from './components/common/NotInPlan';

// Layouts
import AuthLayout from './components/auth/AuthLayout';
import DashboardLayout from './components/common/DashboardLayout';

// Auth pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';

// Dashboard pages
import DashboardPage from './pages/DashboardPage';
import TicketsPage from './pages/TicketsPage';
import TicketDetailPage from './pages/TicketDetailPage';
import InvoicesPage from './pages/InvoicesPage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import PaymentsPage from './pages/PaymentsPage';
import QuotesPage from './pages/QuotesPage';
import QuoteDetailPage from './pages/QuoteDetailPage';
import LocalQuoteDetailPage from './pages/LocalQuoteDetailPage';
import AgreementsPage from './pages/AgreementsPage';
import AgreementDetailPage from './pages/AgreementDetailPage';
import ConfigurationsPage from './pages/ConfigurationsPage';
import ConfigurationDetailPage from './pages/ConfigurationDetailPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ProfilePage from './pages/ProfilePage';
import TeamPage from './pages/TeamPage';

// Documentation pages
import DocumentationPage from './pages/documentation/DocumentationPage';
import ArticleDetailPage from './pages/documentation/ArticleDetailPage';
import ArticleEditorPage from './pages/documentation/ArticleEditorPage';
import DocConfigurationsPage from './pages/documentation/ConfigurationsPage';
import DocConfigurationDetailPage from './pages/documentation/ConfigurationDetailPage';
import ExpirationsPage from './pages/documentation/ExpirationsPage';
import FlexibleAssetsPage from './pages/documentation/FlexibleAssetsPage';
import FlexibleAssetDetailPage from './pages/documentation/FlexibleAssetDetailPage';
import GlobalSearchPage from './pages/documentation/GlobalSearchPage';
import ActivityFeedPage from './pages/documentation/ActivityFeedPage';

// Vault pages
import VaultPage from './pages/vault/VaultPage';
import PasswordDetailPage from './pages/vault/PasswordDetailPage';
import PasswordEditorPage from './pages/vault/PasswordEditorPage';

// Admin pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import UserDetailPage from './pages/admin/UserDetailPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import SessionsPage from './pages/admin/SessionsPage';
import CompaniesPage from './pages/admin/CompaniesPage';
import CompanyDetailPage from './pages/admin/CompanyDetailPage';
import EmailTemplatesPage from './pages/admin/EmailTemplatesPage';
import EmailTemplateEditorPage from './pages/admin/EmailTemplateEditorPage';
import ApiKeysPage from './pages/admin/ApiKeysPage';
import ApiKeyDetailPage from './pages/admin/ApiKeyDetailPage';
import AdminQuotesPage from './pages/admin/AdminQuotesPage';
import AdminQuoteEditorPage from './pages/admin/AdminQuoteEditorPage';
import AdminQuoteDetailPage from './pages/admin/AdminQuoteDetailPage';
import QuoteSettingsPage from './pages/admin/QuoteSettingsPage';
import AdminInvoicesPage from './pages/admin/AdminInvoicesPage';
import AdminInvoiceDetailPage from './pages/admin/AdminInvoiceDetailPage';
import AdminAgreementsPage from './pages/admin/AdminAgreementsPage';
import AdminTicketsPage from './pages/admin/AdminTicketsPage';
import AdminTimePage from './pages/admin/AdminTimePage';
import AdminContractsPage from './pages/admin/AdminContractsPage';
import AdminContractDetailPage from './pages/admin/AdminContractDetailPage';
import ContractSettingsPage from './pages/admin/ContractSettingsPage';
import ContractsPage from './pages/ContractsPage';
import SignContractPage from './pages/SignContractPage';
import AdminFeatureFlagsPage from './pages/admin/AdminFeatureFlagsPage';
import AdminAssetTypesPage from './pages/admin/AdminAssetTypesPage';
import AdminRolesPage from './pages/admin/AdminRolesPage';
import AdminClientPaymentsPage from './pages/admin/AdminClientPaymentsPage';
import PortalSettingsPage from './pages/admin/PortalSettingsPage';
import HelpPage from './pages/HelpPage';
import IntegrationsPage from './pages/admin/integrations/IntegrationsPage';
import DomainsPage from './pages/admin/DomainsPage';
import TicketAlertsPage from './pages/admin/integrations/TicketAlertsPage';

// Public quote view (no auth required)
import QuoteViewPage from './pages/QuoteViewPage';
import PaymentActionPage from './pages/PaymentActionPage';

// Platform operator console. Its own login, its own token (a separate JWT
// audience) and its own layout: an operator is not a tenant user.
import ManageLayout from './pages/manage/ManageLayout';
import ManageLoginPage from './pages/manage/ManageLoginPage';
import TenantsPage from './pages/manage/TenantsPage';
import TenantDetailPage from './pages/manage/TenantDetailPage';
import PlatformSetupPage from './pages/manage/PlatformSetupPage';
import ReleasesPage from './pages/manage/ReleasesPage';
import { isInternalRole } from './utils/roles.js';
import { useBrandingStore } from './context/brandingStore';
import NoPortalHere from './components/common/NoPortalHere';

const isManageHost =
  typeof window !== 'undefined' &&
  (/^manage\./i.test(window.location.hostname) || /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname));

// The real console host serves the console and nothing else: with one tenant it
// would otherwise resolve to that tenant and show its portal at `/`. Localhost
// keeps both for development.
const isConsoleOnlyHost = typeof window !== 'undefined' && /^manage\./i.test(window.location.hostname);

/**
 * Protected route wrapper
 * Requires authentication (users can only be authenticated if email is verified)
 */
function ProtectedRoute({ children }) {
  const { isAuthenticated, isInitializing } = useAuthStore();

  // Wait for auth state to be restored from storage before making redirect decisions
  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

/**
 * Guest route wrapper (redirect if authenticated)
 */
function GuestRoute({ children }) {
  const { isAuthenticated, isInitializing } = useAuthStore();

  // Only show loading during initial auth check, not during login attempts
  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

/**
 * Role-based route wrapper
 * Requires user to have at least the specified minimum role
 */
function RoleBasedRoute({ children, minRole }) {
  const { isAuthenticated, isInitializing, hasMinRole } = useAuthStore();

  // Wait for auth state to be restored from storage before making redirect decisions
  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasMinRole(minRole)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-neutral-900 mb-4">403</h1>
          <p className="text-neutral-600 mb-6">You don't have permission to access this page</p>
          <a href="/dashboard" className="btn-primary">Go to Dashboard</a>
        </div>
      </div>
    );
  }

  return children;
}

/**
 * Check if internal staff has permission for a feature based on their role
 * permissions. `permissionOf` is the backend's flag → permission map,
 * served by /api/feature-flags/my-flags so that there is one copy of it.
 * A flag with no mapping is refused: the copy this file used to carry was
 * eight keys short of the backend's and let every unknown flag through.
 */
function hasPermissionForFeature(user, flagKey, permissionOf) {
  const userRole = user?.role;
  const permissions = user?.permissions || {};

  // Admin role has full access to all features
  if (userRole === 'admin') {
    return true;
  }

  const permissionName = permissionOf?.[flagKey];
  if (!permissionName) {
    return false;
  }

  // Check admin permissions array first (for admin section access)
  const adminPermissions = permissions.admin || [];
  if (adminPermissions.includes(permissionName)) {
    return true;
  }

  // Check resource-level permissions (for module access)
  const resourcePermissions = permissions[permissionName] || [];
  if (resourcePermissions.length > 0) {
    // Having any permission on the resource grants module access
    return true;
  }

  return false;
}

/**
 * Feature-gated route wrapper. Three answers, in order:
 *   1. the plan does not include the module → "Not included in your plan",
 *      for everyone, admins included (the backend refuses them too);
 *   2. internal staff → their role's permission for the module;
 *   3. a customer → redirected to the dashboard when the flag is off.
 */
function FeatureRoute({ children, flag }) {
  const isEnabled = useFeatureFlag(flag);
  const locked = useFlagLocked(flag);
  const permissionOf = usePermissionOf();
  const { isLoaded, isLoading } = useFeatureFlagStatus();
  const { user, can } = useAuthStore();
  const isInternalStaff = isInternalRole(user?.role);

  // Wait for feature flags to load before making redirect decision
  if (!isLoaded || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (locked) {
    return <NotInPlan feature={flag} />;
  }

  // For internal staff, check role permissions instead of feature flags
  if (isInternalStaff) {
    const hasPermission = hasPermissionForFeature(user, flag, permissionOf);
    if (!hasPermission) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-neutral-900 mb-4">403</h1>
            <p className="text-neutral-600 mb-6">You don't have permission to access this feature</p>
            <a href="/dashboard" className="btn-primary">Go to Dashboard</a>
          </div>
        </div>
      );
    }
    return children;
  }

  // For customer users, check feature flags
  if (!isEnabled) {
    return <Navigate to="/dashboard" replace />;
  }

  // ...and the customer's role (backend services/customerAccess.js).
  const perm = CUSTOMER_PERMISSION_OF_FLAG[flag];
  if (perm && !can(...perm)) return <NotInRole />;

  return children;
}

/**
 * The customer permission each module page needs, by its module flag. The
 * server refuses the data regardless; this is so the page says why instead
 * of rendering empty.
 */
const CUSTOMER_PERMISSION_OF_FLAG = {
  'module.payments': ['payments', 'read'],
  'module.quotes': ['quotes', 'read'],
  'module.agreements': ['agreements', 'read'],
  'module.configurations': ['assets', 'read'],
  'module.projects': ['projects', 'read'],
  'module.documentation': ['documentation', 'read'],
  'module.vault': ['vault', 'read'],
  'module.team': ['company_users', 'manage'],
};

/** A page outside the customer's role, reached by URL. */
function NotInRole() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-2">Not part of your role</h1>
        <p className="text-neutral-600 dark:text-neutral-400 mb-6">
          Your account does not include this page. Ask your company&apos;s administrator if you need it.
        </p>
        <a href="/dashboard" className="btn-primary">Go to Dashboard</a>
      </div>
    </div>
  );
}

/** Routes with no module flag (Invoices, Tickets): the customer permission alone. */
function CapabilityRoute({ perm, children }) {
  const { can } = useAuthStore();
  return can(...perm) ? children : <NotInRole />;
}

function App() {
  const { isAuthenticated } = useAuthStore();
  const { loadFlags, reset: resetFlags } = useFeatureFlagStore();

  // Load feature flags when user authenticates
  useEffect(() => {
    if (isAuthenticated) {
      loadFlags();
    } else {
      resetFlags();
    }
  }, [isAuthenticated, loadFlags, resetFlags]);

  // The hostname names no tenant (the resolve endpoint said so). The
  // operator console is the one thing served on such a host, so it is
  // exempt; everything else gets a page that says what is wrong instead of
  // a login form that cannot work.
  const noPortal = useBrandingStore((s) => s.status === 'unknown');
  if (noPortal && !isManageHost) return <NoPortalHere />;

  if (isConsoleOnlyHost) {
    return (
      <Routes>
        <Route path="/manage/login" element={<ManageLoginPage />} />
        <Route path="/manage" element={<ManageLayout />}>
          <Route index element={<TenantsPage />} />
          <Route path="tenants/:slug" element={<TenantDetailPage />} />
          <Route path="setup" element={<PlatformSetupPage />} />
          <Route path="releases" element={<ReleasesPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/manage" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/quote/:token" element={<QuoteViewPage />} />
      {/* Phase 7d: the emailed "confirm your payment" link (signed token, no sign-in) */}
      <Route path="/pay/confirm/:token" element={<PaymentActionPage mode="token" />} />
      {/* A contract's emailed signing link (its own token, no sign-in needed) */}
      <Route path="/sign/:token" element={<SignContractPage />} />

      {/* Auth routes (guest only) */}
      <Route element={<GuestRoute><AuthLayout /></GuestRoute>}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>

      {/* Email verification / Complete registration (public - no auth required) */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      {/* Platform operator console (not a tenant area: no GuestRoute/ProtectedRoute).
          Mounted on the manage. hostname only - and localhost for development -
          so an operator never signs in on a tenant origin, where tenant-authored
          HTML could read the token that provisions and suspends every tenant. */}
      {isManageHost && (
        <>
          <Route path="/manage/login" element={<ManageLoginPage />} />
          <Route path="/manage" element={<ManageLayout />}>
            <Route index element={<TenantsPage />} />
            <Route path="tenants/:slug" element={<TenantDetailPage />} />
            <Route path="setup" element={<PlatformSetupPage />} />
            <Route path="releases" element={<ReleasesPage />} />
          </Route>
        </>
      )}

      {/* Protected dashboard routes */}
      <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/tickets" element={<CapabilityRoute perm={['tickets', 'read']}><TicketsPage /></CapabilityRoute>} />
        <Route path="/tickets/:id" element={<CapabilityRoute perm={['tickets', 'read']}><TicketDetailPage /></CapabilityRoute>} />
        <Route path="/invoices" element={<CapabilityRoute perm={['invoices', 'read']}><InvoicesPage /></CapabilityRoute>} />
        <Route path="/invoices/:id" element={<CapabilityRoute perm={['invoices', 'read']}><InvoiceDetailPage /></CapabilityRoute>} />
        <Route
          path="/payments"
          element={
            <FeatureRoute flag="module.payments">
              <PaymentsPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/payments/confirm/:actionId"
          element={
            <FeatureRoute flag="module.payments">
              <PaymentActionPage mode="signed-in" />
            </FeatureRoute>
          }
        />
        <Route
          path="/quotes"
          element={
            <FeatureRoute flag="module.quotes">
              <QuotesPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/quotes/local/:id"
          element={
            <FeatureRoute flag="module.quotes">
              <LocalQuoteDetailPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/quotes/:id"
          element={
            <FeatureRoute flag="module.quotes">
              <QuoteDetailPage />
            </FeatureRoute>
          }
        />
        {/* No customer permission on the route: a person named as a signer sees their contract whatever their role. */}
        <Route path="/contracts" element={<FeatureRoute flag="module.contracts"><ContractsPage /></FeatureRoute>} />
        <Route
          path="/agreements"
          element={
            <FeatureRoute flag="module.agreements">
              <AgreementsPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/agreements/:id"
          element={
            <FeatureRoute flag="module.agreements">
              <AgreementDetailPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/assets"
          element={
            <FeatureRoute flag="module.configurations">
              <ConfigurationsPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/assets/:id"
          element={
            <FeatureRoute flag="module.configurations">
              <ConfigurationDetailPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/projects"
          element={
            <FeatureRoute flag="module.projects">
              <ProjectsPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/projects/:id"
          element={
            <FeatureRoute flag="module.projects">
              <ProjectDetailPage />
            </FeatureRoute>
          }
        />
        <Route path="/profile" element={<ProfilePage />} />
        <Route
          path="/team"
          element={
            <FeatureRoute flag="module.team">
              {/* company_users: manage, checked in FeatureRoute; a custom role
                  holding it could never pass the old minRole="company_admin". */}
              <TeamPage />
            </FeatureRoute>
          }
        />

        {/* Documentation routes */}
        <Route
          path="/documentation"
          element={
            <FeatureRoute flag="module.documentation">
              <DocumentationPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/documentation/search"
          element={
            <FeatureRoute flag="module.documentation">
              <GlobalSearchPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/documentation/articles/new"
          element={
            <FeatureRoute flag="module.documentation">
              <RoleBasedRoute minRole="technician">
                <ArticleEditorPage />
              </RoleBasedRoute>
            </FeatureRoute>
          }
        />
        <Route
          path="/documentation/articles/:id"
          element={
            <FeatureRoute flag="module.documentation">
              <ArticleDetailPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/documentation/articles/:id/edit"
          element={
            <FeatureRoute flag="module.documentation">
              <RoleBasedRoute minRole="technician">
                <ArticleEditorPage />
              </RoleBasedRoute>
            </FeatureRoute>
          }
        />
        <Route
          path="/documentation/configurations"
          element={
            <FeatureRoute flag="module.documentation">
              <DocConfigurationsPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/documentation/configurations/:id"
          element={
            <FeatureRoute flag="module.documentation">
              <DocConfigurationDetailPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/documentation/flexible-assets"
          element={
            <FeatureRoute flag="module.documentation">
              <FlexibleAssetsPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/documentation/flexible-assets/:id"
          element={
            <FeatureRoute flag="module.documentation">
              <FlexibleAssetDetailPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/documentation/expirations"
          element={
            <FeatureRoute flag="module.documentation">
              <ExpirationsPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/documentation/activity"
          element={
            <FeatureRoute flag="module.documentation">
              <RoleBasedRoute minRole="technician">
                <ActivityFeedPage />
              </RoleBasedRoute>
            </FeatureRoute>
          }
        />

        {/* Vault routes */}
        <Route
          path="/vault"
          element={
            <FeatureRoute flag="module.vault">
              <VaultPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/vault/new"
          element={
            <FeatureRoute flag="module.vault">
              <RoleBasedRoute minRole="technician">
                <PasswordEditorPage />
              </RoleBasedRoute>
            </FeatureRoute>
          }
        />
        <Route
          path="/vault/:id"
          element={
            <FeatureRoute flag="module.vault">
              <PasswordDetailPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/vault/:id/edit"
          element={
            <FeatureRoute flag="module.vault">
              <RoleBasedRoute minRole="technician">
                <PasswordEditorPage />
              </RoleBasedRoute>
            </FeatureRoute>
          }
        />

        {/* Admin routes - Role protected */}
        <Route
          path="/admin"
          element={
            <RoleBasedRoute minRole="technician">
              <AdminDashboardPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <RoleBasedRoute minRole="admin">
              <UserManagementPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/users/:userId"
          element={
            <RoleBasedRoute minRole="admin">
              <UserDetailPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/sessions"
          element={
            <RoleBasedRoute minRole="admin">
              <SessionsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/companies"
          element={
            <RoleBasedRoute minRole="technician">
              <CompaniesPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/companies/:companyId"
          element={
            <RoleBasedRoute minRole="technician">
              <CompanyDetailPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <RoleBasedRoute minRole="technician">
              <AuditLogPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/email-templates"
          element={
            <RoleBasedRoute minRole="admin">
              <EmailTemplatesPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/email-templates/:id"
          element={
            <RoleBasedRoute minRole="admin">
              <EmailTemplateEditorPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/api-keys"
          element={
            <RoleBasedRoute minRole="admin">
              <ApiKeysPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/api-keys/:id"
          element={
            <RoleBasedRoute minRole="admin">
              <ApiKeyDetailPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/quotes"
          element={
            <RoleBasedRoute minRole="technician">
              <AdminQuotesPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/quotes/new"
          element={
            <RoleBasedRoute minRole="technician">
              <AdminQuoteEditorPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/quotes/:id/edit"
          element={
            <RoleBasedRoute minRole="technician">
              <AdminQuoteEditorPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/quotes/:id/view"
          element={
            <RoleBasedRoute minRole="technician">
              <AdminQuoteDetailPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/quotes/settings"
          element={
            <RoleBasedRoute minRole="admin">
              <QuoteSettingsPage />
            </RoleBasedRoute>
          }
        />
        {/* Help. Anyone signed in; the page itself decides which pages they
            see, from their role and the module flags. The splat carries the
            page slug, e.g. /help/customer/pay-an-invoice. */}
        <Route path="/help" element={<HelpPage />} />
        <Route path="/help/*" element={<HelpPage />} />
        <Route
          path="/admin/portal-settings"
          element={
            <RoleBasedRoute minRole="admin">
              <PortalSettingsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/domains"
          element={
            <RoleBasedRoute minRole="admin">
              <DomainsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/integrations"
          element={
            <RoleBasedRoute minRole="admin">
              <IntegrationsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/integrations/ticket-alerts"
          element={
            <RoleBasedRoute minRole="admin">
              <TicketAlertsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/invoices"
          element={
            <RoleBasedRoute minRole="technician">
              <AdminInvoicesPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/invoices/:id"
          element={
            <RoleBasedRoute minRole="technician">
              <AdminInvoiceDetailPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/client-payments"
          element={
            <RoleBasedRoute minRole="technician">
              <AdminClientPaymentsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/agreements"
          element={
            <RoleBasedRoute minRole="technician">
              <AdminAgreementsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/agreements/:id"
          element={
            <RoleBasedRoute minRole="technician">
              <AgreementDetailPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/tickets"
          element={
            <RoleBasedRoute minRole="technician">
              <AdminTicketsPage />
            </RoleBasedRoute>
          }
        />
        <Route path="/admin/contracts" element={<RoleBasedRoute minRole="technician"><AdminContractsPage /></RoleBasedRoute>} />
        <Route path="/admin/contracts/settings" element={<RoleBasedRoute minRole="technician"><ContractSettingsPage /></RoleBasedRoute>} />
        <Route path="/admin/contracts/:id" element={<RoleBasedRoute minRole="technician"><AdminContractDetailPage /></RoleBasedRoute>} />
        <Route
          path="/admin/time"
          element={
            <RoleBasedRoute minRole="technician">
              <AdminTimePage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/feature-flags"
          element={
            <RoleBasedRoute minRole="admin">
              <AdminFeatureFlagsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/asset-types"
          element={
            <RoleBasedRoute minRole="admin">
              <AdminAssetTypesPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/roles"
          element={
            <RoleBasedRoute minRole="admin">
              <AdminRolesPage />
            </RoleBasedRoute>
          }
        />
      </Route>

      {/* Redirect root to dashboard or login */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* 404 */}
      <Route path="*" element={
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-neutral-900 mb-4">404</h1>
            <p className="text-neutral-600 mb-6">Page not found</p>
            <a href="/dashboard" className="btn-primary">Go to Dashboard</a>
          </div>
        </div>
      } />
    </Routes>
  );
}

export default App;
